import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastFlash } from '@/lib/flash-toast';

export function CreateMentorDialog({ eventSlug }: { eventSlug: string }) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    fullName: '',
    github: '',
    linkedin: '',
    pictureUrl: '',
    companyTitle: '',
  });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/mentors`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Mentor created');
        reset();
        setOpen(false);
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to create mentor' }),
    });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Mentor
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Mentor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-mentor-full-name">Full Name</Label>
              <Input
                id="create-mentor-full-name"
                placeholder="John Doe"
                value={data.fullName}
                onChange={(e) => setData('fullName', e.target.value)}
              />
              {errors.fullName && (
                <p className="text-destructive text-sm">{errors.fullName}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-mentor-github">GitHub URL</Label>
              <Input
                id="create-mentor-github"
                placeholder="https://github.com/username"
                value={data.github}
                onChange={(e) => setData('github', e.target.value)}
              />
              {errors.github && (
                <p className="text-destructive text-sm">{errors.github}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-mentor-linkedin">
                LinkedIn URL (optional)
              </Label>
              <Input
                id="create-mentor-linkedin"
                placeholder="https://linkedin.com/in/username"
                value={data.linkedin}
                onChange={(e) => setData('linkedin', e.target.value)}
              />
              {errors.linkedin && (
                <p className="text-destructive text-sm">{errors.linkedin}</p>
              )}
            </div>
            <p className="text-muted-foreground text-xs">
              You'll be able to upload a picture after creating the mentor.
            </p>
            <div className="grid gap-2">
              <Label htmlFor="create-mentor-company-title">
                Company / Title (optional)
              </Label>
              <Input
                id="create-mentor-company-title"
                placeholder="Software Engineer at Acme Inc"
                value={data.companyTitle}
                onChange={(e) => setData('companyTitle', e.target.value)}
              />
              {errors.companyTitle && (
                <p className="text-destructive text-sm">
                  {errors.companyTitle}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create Mentor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
